import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import type { HttpError } from "@/lib/network/json-request";
import { jsonRequest } from "@/lib/network/json-request";

type Meeting = {
  dateTime: string;
  requestId: string;
  status: "INITIATED" | "RESCHEDULED" | "CANCELED";
};

type UseLssMeetingProps = Omit<
  UseQueryOptions<Meeting, Error>,
  "queryKey" | "queryFn"
> & {
  requestId?: string;
};

async function request({
  requestId,
}: {
  requestId?: string;
}): Promise<Meeting> {
  return jsonRequest(
    `${process.env.NEXT_PUBLIC_BOOKIT_BASE_URL}/appointments/${requestId}/datetime`,
    {
      method: "GET",
    },
  );
}

/**
 * Returns a query hook for fetching an LSS meeting.
 *
 * @param requestId - The bookit request ID of the LSS meeting to fetch.
 */
export function useLssMeeting({ requestId, ...options }: UseLssMeetingProps) {
  const query = useQuery<Meeting, HttpError>({
    ...options,
    queryKey: ["fetch-lss-meeting", requestId],
    queryFn: () => request({ requestId }),
    enabled: Boolean(options.enabled ?? requestId),
  });

  // After the LSS meeting happens, the bookit request status changes to "fulfilled",
  // and the API call will return a 423 error.
  const isRequestFulfilled = query.error?.status === 423;

  return {
    ...query,
    isRequestFulfilled,
  };
}
